import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Globe, Search, Download, RefreshCw, Activity, ShieldCheck, Plus, Trash2, Link2,
  CornerDownRight, Upload, X,
  AlertTriangle, CheckCircle, XCircle, CircleDashed, Cloud, ChevronDown, ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';
import {
  domains as api,
  type Domain, type Subdomain, type MonitorInterval, type DomainCheckResult, type DomainOwnership,
  type DnsImportBericht } from '../api';
import { Blaetterleiste, TabellenKopf, useTabelle, type FilterDefinition, type SpaltenDefinition } from '../components/TabellenSteuerung';

/**
 * Domainübersicht.
 *
 * Der Menüpunkt heisst „Domains", und das ist bewusst nicht „Domainverwaltung":
 * **hier wird nichts verwaltet.** Registriert, verlängert und gekündigt wird bei
 * adns2 und united-domains. Diese Seite beantwortet drei Fragen — wo liegt was,
 * worauf zeigt es, läuft es.
 *
 * Daraus folgt, was es hier *nicht* gibt: keinen Knopf „verlängern", kein
 * „übertragen", kein „kündigen". Ein Löschen entfernt den Eintrag aus dieser
 * Liste und rührt die Domain nicht an — die Meldung sagt das auch.
 */

const ERGEBNIS: Record<DomainCheckResult, { text: string; farbe: string; Symbol: typeof CheckCircle }> = {
  OK:          { text: 'erreichbar',      farbe: 'bg-emerald-100 text-emerald-800', Symbol: CheckCircle },
  PLACEHOLDER: { text: 'Standardseite',   farbe: 'bg-amber-100 text-amber-800',     Symbol: AlertTriangle },
  REDIRECT:    { text: 'Weiterleitung',   farbe: 'bg-slate-100 text-slate-600',     Symbol: CircleDashed },
  HTTP_ERROR:  { text: 'HTTP-Fehler',     farbe: 'bg-red-100 text-red-800',         Symbol: XCircle },
  // Cloudflare hat geantwortet, der Ursprungsserver nicht (520-527). DNS und
  // Zertifikat stimmen also — die Maschine dahinter ist tot.
  ORIGIN_DOWN: { text: 'Ursprung tot',    farbe: 'bg-red-100 text-red-800',         Symbol: XCircle },
  // Cloudflare hat UNSEREN Prüfer abgewiesen. Die Seite kann tadellos laufen;
  // deshalb amber und nicht rot.
  BLOCKED:     { text: 'Prüfer geblockt', farbe: 'bg-amber-100 text-amber-800',     Symbol: AlertTriangle },
  TLS_ERROR:   { text: 'Zertifikat',      farbe: 'bg-red-100 text-red-800',         Symbol: XCircle },
  TIMEOUT:     { text: 'Zeitüberschreitung', farbe: 'bg-red-100 text-red-800',      Symbol: XCircle },
  DNS_ERROR:   { text: 'löst nicht auf',  farbe: 'bg-red-100 text-red-800',         Symbol: XCircle },
  CONN_ERROR:  { text: 'keine Verbindung', farbe: 'bg-red-100 text-red-800',        Symbol: XCircle },
};

const TAKTE: { wert: MonitorInterval; label: string }[] = [
  { wert: 'OFF', label: 'aus' },
  { wert: 'HOURLY', label: 'stündlich' },
  { wert: 'TWELVE_HOURS', label: 'alle 12 Stunden' },
  { wert: 'DAILY', label: 'täglich' },
];

/**
 * Der Eigentumsstatus als Auswahlfeld direkt in der Zeile.
 *
 * Bewusst kein Dialog: eine Domain einzuordnen ist etwas, das man beim
 * Durchsehen einer Liste tut — vierhundert Mal einen Dialog zu öffnen und zu
 * schliessen würde niemand durchhalten. Gespeichert wird sofort.
 */
const EIGENTUM: DomainOwnership[] = ['UNDECIDED', 'RENEW', 'HOLD', 'PARKED', 'REVIEW', 'TRANSFER', 'DROP'];

const EIGENTUM_TEXT: Record<DomainOwnership, string> = {
  UNDECIDED: 'nicht bewertet',
  RENEW: 'verlängern',
  HOLD: 'halten',
  PARKED: 'geparkt',
  REVIEW: 'prüfen',
  TRANSFER: 'umziehen',
  DROP: 'abstoßen',
};

const EIGENTUM_FARBE: Record<DomainOwnership, string> = {
  // Unbewertet bleibt farblos: es ist kein Zustand, sondern eine offene Frage,
  // und eine Farbe würde ihm ein Gewicht geben, das er nicht hat.
  UNDECIDED: 'border-slate-200 text-slate-400',
  RENEW: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  HOLD: 'border-teal-200 bg-teal-50 text-teal-700',
  PARKED: 'border-slate-300 bg-slate-50 text-slate-600',
  REVIEW: 'border-amber-200 bg-amber-50 text-amber-700',
  TRANSFER: 'border-sky-200 bg-sky-50 text-sky-700',
  DROP: 'border-rose-200 bg-rose-50 text-rose-700',
};

/**
 * Der letzte Kündigungstag: Ablauf minus Frist.
 *
 * Gerechnet, nicht gespeichert — er verschiebt sich mit jeder Verlängerung.
 * `null`, solange eines von beidem fehlt; dann steht in der Spalte ein Strich
 * und keine erfundene Zahl.
 */
function kuendbarBis(d: Domain): Date | null {
  if (!d.expiresAt || d.noticeDays === null || d.noticeDays === undefined) return null;
  const t = new Date(d.expiresAt);
  t.setDate(t.getDate() - d.noticeDays);
  return t;
}

/** Tage bis zum letzten Kündigungstag. Negativ heisst: Frist ist vorbei. */
function tageBisFrist(d: Domain): number | null {
  const t = kuendbarBis(d);
  if (!t) return null;
  return Math.ceil((t.getTime() - Date.now()) / 86_400_000);
}

function BewertungsWahl({ wert, aendern }: {
  wert: DomainOwnership;
  aendern: (w: DomainOwnership) => void;
}) {
  return (
    <select
      value={wert}
      onChange={(e) => aendern(e.target.value as DomainOwnership)}
      onClick={(e) => e.stopPropagation()}
      className={`text-xs rounded-lg border px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500 ${EIGENTUM_FARBE[wert]}`}
    >
      {EIGENTUM.map((b) => (
        <option key={b} value={b}>{EIGENTUM_TEXT[b]}</option>
      ))}
    </select>
  );
}

/**
 * Die Frist als Text mit Warnung.
 *
 * DER TEUERSTE FALL IST „abstoßen, Frist verpasst": die Domain verlängert sich
 * dann still um eine ganze Periode, und niemand merkt es, bis die Rechnung
 * kommt. Deshalb wird bei `DROP` gewarnt, sobald es eng wird — und deutlich
 * markiert, wenn es zu spät ist.
 */
function FristAnzeige({ d }: { d: Domain }) {
  const tage = tageBisFrist(d);
  if (tage === null) return <span className="text-slate-300">—</span>;

  const datum = kuendbarBis(d)!.toLocaleDateString('de-DE');
  const abzustossen = d.ownership === 'DROP';
  const farbe =
    abzustossen && tage < 0 ? 'text-rose-700 font-semibold'
    : abzustossen && tage <= 30 ? 'text-amber-700 font-semibold'
    : tage < 0 ? 'text-slate-400'
    : 'text-slate-600';

  return (
    <span className={`text-xs ${farbe}`} title={
      tage < 0
        ? `Frist am ${datum} abgelaufen — die Domain verlängert sich`
        : `noch ${tage} Tage bis zum letzten Kündigungstag`
    }>
      {datum}
      {abzustossen && tage < 0 && ' ⚠'}
    </span>
  );
}

function ErgebnisMarke({ result }: { result?: DomainCheckResult | null }) {
  if (!result) return <span className="text-xs text-slate-400">nie geprüft</span>;

  // NICHT BLIND NACHSCHLAGEN. Das Backend kann einen Wert liefern, den diese
  // Tabelle noch nicht kennt — genau das ist passiert, als ORIGIN_DOWN und
  // BLOCKED dazukamen: die Destrukturierung von `undefined` hat die ganze Seite
  // ausgeknipst. Ein unbekannter Zustand soll dastehen, nicht abstürzen.
  const eintrag = ERGEBNIS[result];
  if (!eintrag) {
    return (
      <span
        title="Dieser Zustand ist der Oberfläche nicht bekannt — vermutlich neuer als sie."
        className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
      >
        <CircleDashed size={12} />
        {result}
      </span>
    );
  }
  const { text, farbe, Symbol } = eintrag;
  return (
    <span className={clsx('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs', farbe)}>
      <Symbol size={12} />
      {text}
    </span>
  );
}

export default function DomainsPage() {
  const qc = useQueryClient();
  const [offen, setOffen] = useState<string | null>(null);

  /**
   * Angehakte Domains.
   *
   * Ein Set und keine Liste: An- und Abhaken ist die haeufigste Bewegung, und
   * bei vierhundert Zeilen ist ein `includes` je Zeile und Bildaufbau spuerbar.
   */
  /**
   * Was im Suchfeld und in den Auswahlfeldern steht — noch nicht angewandt.
   *
   * WARUM GETRENNT: vorher wirkte jeder Tastendruck sofort. Bei 423 Zeilen ist
   * das technisch kein Problem, aber es ist nicht abzusehen, wann eine Eingabe
   * fertig ist — die Liste zappelt beim Tippen, und ob ein Filter nun greift
   * oder man sich vertippt hat, steht nirgends. Ein Knopf sagt es.
   */
  const [entwurfSuche, setEntwurfSuche] = useState('');
  const [entwurfFilter, setEntwurfFilter] = useState<Record<string, string>>({});

  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
  const [massenLaeuft, setMassenLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neueDomain, setNeueDomain] = useState('');
  const [formularOffen, setFormularOffen] = useState(false);

  // Alles auf einmal: bei gut vierhundert Zeilen ist das eine Abfrage und
  // spart die Seitenblätterung, die beim Suchen ohnehin im Weg stünde.
  const { data, isLoading } = useQuery({
    queryKey: ['domains'],
    // Ohne `limit`: alleSeiten() holt alle Seiten. Ein `limit: 1000` stand hier
    // und wurde vom Backend auf 200 geklemmt -- danach war bei Domain 200
    // Schluss, ohne dass irgendwo etwas fehlte.
    queryFn: () => api.list(),
  });
  const { data: anbieter } = useQuery({ queryKey: ['hosting-providers'], queryFn: api.providers.list });

  // Anbieter-Id -> Anmeldeadresse, damit die Zelle nicht bei jeder Zeile suchen muss.
  const anbieterLogin: Record<string, string> = Object.fromEntries(
    (anbieter ?? []).filter((a) => a.loginUrl).map((a) => [a.id, a.loginUrl as string]),
  );

  const zeilen: Domain[] = data ?? [];

  const spalten: SpaltenDefinition<Domain>[] = [
    { key: 'name', label: 'Domain', wert: (d) => d.unicodeName ?? d.name },
    { key: 'anbieter', label: 'Anbieter', wert: (d) => d.provider?.name ?? '' },
    { key: 'ziel', label: 'Ziel laut Registrar', wert: (d) => d.registrarTarget ?? '' },
    { key: 'subdomains', label: 'Sub', wert: (d) => d._count?.subdomains ?? 0, numerisch: true },
    { key: 'monitoring', label: 'Prüfung', wert: (d) => d.monitorInterval },
    { key: 'ergebnis', label: 'Zustand', wert: (d) => d.lastResult ?? '' },
    { key: 'ablauf', label: 'Läuft ab', wert: (d) => (d.expiresAt ? new Date(d.expiresAt) : null) },
    // Sortiert nach dem gerechneten Stichtag, nicht nach der Frist in Tagen:
    // gesucht ist "was steht als naechstes an", nicht "wo ist die Frist lang".
    { key: 'frist', label: 'Kündbar bis', wert: (d) => kuendbarBis(d) },
    { key: 'eigentum', label: 'Eigentum', wert: (d) => EIGENTUM_TEXT[d.ownership] },
  ];

  const filter: FilterDefinition<Domain>[] = [
    {
      key: 'anbieter',
      label: 'Anbieter',
      optionen: (anbieter ?? []).map((a) => ({ wert: a.id, label: a.name })),
      passt: (d, w) => d.providerId === w,
    },
    {
      key: 'zustand',
      label: 'Zustand',
      optionen: Object.entries(ERGEBNIS).map(([k, v]) => ({ wert: k, label: v.text })),
      passt: (d, w) => d.lastResult === w,
    },
    {
      key: 'cloudflare',
      label: 'Cloudflare',
      optionen: [
        { wert: 'ja', label: 'mit Tunnel' },
        { wert: 'nein', label: 'ohne Tunnel' },
        { wert: 'abweichend', label: 'erkannt, nicht gesetzt' },
      ],
      passt: (d, w) =>
        w === 'ja' ? d.cloudflareTunnel
        : w === 'nein' ? !d.cloudflareTunnel
        : d.cloudflareDetected && !d.cloudflareTunnel,
    },
    {
      key: 'takt',
      label: 'Prüfung',
      optionen: TAKTE.map((t) => ({ wert: t.wert, label: t.label })),
      passt: (d, w) => d.monitorInterval === w,
    },
    {
      key: 'eigentum',
      label: 'Eigentum',
      optionen: EIGENTUM.map((e) => ({ wert: e, label: EIGENTUM_TEXT[e] })),
      passt: (d, w) => d.ownership === w,
    },
    {
      // Die Frage, für die es das Feld gibt: was muss ich jetzt kündigen,
      // bevor es sich still verlängert?
      key: 'frist',
      label: 'Frist',
      optionen: [
        { wert: 'bald', label: 'läuft in 30 Tagen ab' },
        { wert: 'verpasst', label: 'abstoßen, Frist vorbei' },
        { wert: 'ohne', label: 'keine Frist hinterlegt' },
      ],
      passt: (d, w) => {
        const tage = tageBisFrist(d);
        if (w === 'ohne') return tage === null;
        if (tage === null) return false;
        if (w === 'bald') return tage >= 0 && tage <= 30;
        return d.ownership === 'DROP' && tage < 0;
      },
    },
  ];

  const tabelle = useTabelle({
    speichern: 'domains',
    zeilen,
    spalten,
    // WURDE HIER VERGESSEN. Die Auswahlfelder standen da, `setFilter` lief,
    // `filterWerte` füllte sich — nur bekam useTabelle die Definitionen nie,
    // und ohne `passt` filtert es nichts. Ein Filter, der sichtbar reagiert
    // und nichts tut, ist schlimmer als keiner.
    filter,
    // Auch die IP durchsuchbar machen — „was liegt eigentlich alles auf dieser
    // Kiste?" ist die Frage, die man hier am häufigsten hat.
    suchfelder: (d: Domain) => [
      d.name,
      d.unicodeName ?? '',
      d.registrarTarget ?? '',
      d.provider?.name ?? '',
      d.notes ?? '',
      ...(d.subdomains ?? []).map((s) => `${s.fqdn} ${s.resolvedIp ?? ''}`),
    ],
    startSortierung: { key: 'name', richtung: 'auf' },
  });

  const tun = async (fn: () => Promise<unknown>, gut?: string) => {
    setFehler(null); setMeldung(null);
    try {
      await fn();
      // BEIDE SCHLUESSEL. Der Detailbereich und die Subdomain-Zeilen haengen an
      // `['domain', id]`, die Liste an `['domains']`. Vorher wurde nur die
      // Liste verworfen -- eine neu angelegte Subdomain erschien deshalb erst
      // nach einem Neuladen der Seite.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['domains'] }),
        qc.invalidateQueries({ queryKey: ['domain'] }),
      ]);
      if (gut) setMeldung(gut);
    } catch (e: any) {
      setFehler(e?.response?.data?.error ?? e?.message ?? 'Unbekannter Fehler');
    }
  };

  /**
   * Exportiert, was in der Tabelle steht — mit Suche, Filtern und allem.
   *
   * Vorher ging immer der ganze Bestand heraus, egal was gefiltert war. Wer
   * nach „alles bei united-domains, das abgestossen werden soll" filtert und
   * dann exportiert, erwartet diese Liste und nicht vierhundert Zeilen.
   */
  /** Steht etwas im Entwurf, das noch nicht wirkt? */
  // Gemerkte Filter in den Entwurf übernehmen, sonst stünde beim Öffnen „nicht angewandt“ da.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setEntwurfSuche(tabelle.suche); setEntwurfFilter(tabelle.filterWerte); }, []);

  const nichtAngewandt =
    entwurfSuche !== tabelle.suche ||
    filter.some((f) => (entwurfFilter[f.key] ?? '') !== (tabelle.filterWerte[f.key] ?? ''));

  const anwenden = () => {
    tabelle.setSuche(entwurfSuche);
    for (const f of filter) tabelle.setFilter(f.key, entwurfFilter[f.key] ?? '');
  };

  const umschalten = (id: string) =>
    setGewaehlt((g) => {
      const n = new Set(g);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  // Wählt aus, was gerade zu sehen ist -- nicht den ganzen Bestand. Ein Haken
  // in der Kopfzeile, der 423 Domains auswählt, während 12 auf dem Bildschirm
  // stehen, ist eine Falle.
  const seiteIds = tabelle.sichtbar.map((d) => d.id);
  const alleDerSeite = seiteIds.length > 0 && seiteIds.every((i) => gewaehlt.has(i));

  const seiteUmschalten = () =>
    setGewaehlt((g) => {
      const n = new Set(g);
      if (alleDerSeite) seiteIds.forEach((i) => n.delete(i));
      else seiteIds.forEach((i) => n.add(i));
      return n;
    });

  /**
   * Eine Aktion auf die Auswahl.
   *
   * Die Auswahl bleibt nach dem Ausführen stehen. Wer gerade zwanzig Domains
   * zusammengesucht hat, um sie zu prüfen, will sie danach oft auch bewerten --
   * sie ihm wegzunehmen hiesse, ihn zweimal suchen zu lassen.
   */
  const massenAktion = async (aktion: string, wert?: string) => {
    if (gewaehlt.size === 0) return;
    setMassenLaeuft(true);
    await tun(async () => {
      const r = await api.massenAktion({ ids: [...gewaehlt], aktion, wert });
      const teile = [`${r.betroffen} von ${r.angefragt}`];
      if (r.nichtGefunden > 0) teile.push(`${r.nichtGefunden} nicht gefunden`);
      if (r.hinweis) teile.push(r.hinweis);
      setMeldung(teile.join(' — '));
    });
    setMassenLaeuft(false);
  };

  const exportieren = async () => {
    const ids = tabelle.alleGefilterten.map((d) => d.id);
    // Eine leere Auswahl weist das Backend ab (400). Der Knopf ist dann zwar
    // ausgegraut, aber die Bedingung gehoert auch hierher: der Knopf ist die
    // Anzeige, nicht die Regel.
    if (ids.length === 0) return;
    const blob = await api.exportCsvAuswahl(ids);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const teil = tabelle.eingeschraenkt ? '-auswahl' : '';
    a.download = `domains${teil}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const abweichend = zeilen.filter((d) => d.cloudflareDetected && !d.cloudflareTunnel).length;
  const auffaellig = zeilen.filter((d) => d.lastResult && d.lastResult !== 'OK').length;
  const ueberwacht = zeilen.filter((d) => d.monitorInterval !== 'OFF').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <Globe size={20} /> Domains
        </h1>
        <span className="text-sm text-slate-500">
          {zeilen.length} Domains · {ueberwacht} überwacht
          {auffaellig > 0 && <span className="ml-1 text-amber-700">· {auffaellig} auffällig</span>}
        </span>
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => setFormularOffen((v) => !v)}
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            <Plus size={14} /> Domain
          </button>
          <button
            onClick={exportieren}
            disabled={tabelle.gefiltert === 0}
            title={
              tabelle.eingeschraenkt
                ? `${tabelle.gefiltert} von ${tabelle.gesamt} Domains — genau das, was hier steht`
                : 'Alle Domains'
            }
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Download size={14} />
            CSV
            {tabelle.eingeschraenkt && (
              <span className="text-slate-500">({tabelle.gefiltert})</span>
            )}
          </button>
        </div>
      </div>

      {fehler && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</div>}
      {meldung && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{meldung}</div>}

      {/* Der Hinweis steht oben und nicht im Kleingedruckten: er erklärt, warum
          es hier keinen Knopf „verlängern" gibt. */}
      <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
        Diese Seite <b>verwaltet keine Domains</b> — registriert, verlängert und gekündigt wird
        beim Registrar. Sie zeigt, wo eine Domain liegt, worauf sie zeigt und ob sie erreichbar
        ist. Entfernen löscht nur den Eintrag hier.
      </p>

      {abweichend > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <Cloud size={16} className="mt-0.5 shrink-0" />
          <span>
            Bei <b>{abweichend}</b> Domains spricht der DNS-Befund für Cloudflare, ohne dass das
            Tunnel-Kennzeichen gesetzt ist. Das muss nichts heissen — Cloudflare lässt sich auch
            ohne Tunnel benutzen. Über den Filter <i>Cloudflare → erkannt, nicht gesetzt</i>{' '}
            durchsehen.
          </span>
        </div>
      )}

      {formularOffen && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            tun(async () => {
              await api.create({ name: neueDomain.trim() });
              setNeueDomain('');
              setFormularOffen(false);
            }, 'Domain aufgenommen.');
          }}
          className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-surface p-3"
        >
          <div>
            <label className="mb-1 block text-xs text-slate-500">Domainname</label>
            <input
              autoFocus required value={neueDomain}
              onChange={(e) => setNeueDomain(e.target.value)}
              placeholder="beispiel.de"
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
            />
          </div>
          <button className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700">
            Aufnehmen
          </button>
        </form>
      )}

      {/* Suche und Filter — angewandt wird auf Knopfdruck, nicht beim Tippen. */}
      <form
        onSubmit={(e) => { e.preventDefault(); anwenden(); }}
        className="flex flex-wrap items-center gap-2"
      >
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input
            value={entwurfSuche}
            onChange={(e) => setEntwurfSuche(e.target.value)}
            placeholder="Domain, Subdomain oder IP…"
            className="w-72 rounded-lg border border-slate-300 py-1.5 pl-8 pr-3 text-sm"
          />
        </div>
        {filter.map((f) => (
          <select
            key={f.key}
            value={entwurfFilter[f.key] ?? ''}
            onChange={(e) => setEntwurfFilter((v) => ({ ...v, [f.key]: e.target.value }))}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value="">{f.label}: alle</option>
            {f.optionen.map((o) => (
              <option key={o.wert} value={o.wert}>{o.label}</option>
            ))}
          </select>
        ))}

        <button
          type="submit"
          disabled={!nichtAngewandt}
          title={nichtAngewandt ? 'Die Eingaben anwenden' : 'Nichts geändert'}
          className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Search size={14} /> Filtern
        </button>

        {/* Sagt, dass etwas dasteht, was noch nicht wirkt. Ohne diesen Hinweis
            fragt man sich, warum die Liste nicht reagiert. */}
        {nichtAngewandt && (
          <span className="text-xs text-amber-700">noch nicht angewandt</span>
        )}

        {tabelle.eingeschraenkt && (
          <button
            type="button"
            onClick={() => {
              setEntwurfSuche('');
              setEntwurfFilter({});
              tabelle.zuruecksetzen();
            }}
            className="text-sm text-brand-700 hover:underline"
          >
            {tabelle.gefiltert} von {tabelle.gesamt} — alles zeigen
          </button>
        )}
      </form>

      {/*
        Die Massenleiste erscheint nur, wenn etwas ausgewählt ist. Eine Leiste
        mit ausgegrauten Knöpfen über einer Tabelle, in der nichts angehakt ist,
        ist dauerhaftes Rauschen für einen seltenen Fall.
      */}
      {gewaehlt.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2">
          <span className="text-sm font-medium text-brand-800">
            {gewaehlt.size} ausgewählt
          </span>

          <button
            onClick={() => massenAktion('pruefen')}
            disabled={massenLaeuft}
            title="Erreichbarkeit prüfen — samt aller Subdomains dieser Domains"
            className="flex items-center gap-1 rounded-lg border border-slate-300 bg-surface px-2.5 py-1 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            <Activity size={13} /> Zustand prüfen
          </button>

          <button
            onClick={() => {
              if (!confirm(
                `${gewaehlt.size} Domains crawlen?\n\n` +
                'Das ruft die Seiten selbst ab und dauert Minuten je Domain.',
              )) return;
              massenAktion('crawlen');
            }}
            disabled={massenLaeuft}
            title="URL-Bestandsaufnahme starten — erzeugt Last auf den Zielservern"
            className="flex items-center gap-1 rounded-lg border border-slate-300 bg-surface px-2.5 py-1 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            <Link2 size={13} /> Crawlen
          </button>

          <span className="mx-1 h-5 w-px bg-brand-200" />

          <button
            onClick={() => massenAktion('cloudflareAn')}
            disabled={massenLaeuft}
            className="flex items-center gap-1 rounded-lg border border-slate-300 bg-surface px-2.5 py-1 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            <Cloud size={13} /> Tunnel an
          </button>
          <button
            onClick={() => massenAktion('cloudflareAus')}
            disabled={massenLaeuft}
            className="flex items-center gap-1 rounded-lg border border-slate-300 bg-surface px-2.5 py-1 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            <Cloud size={13} className="opacity-40" /> Tunnel aus
          </button>

          <select
            value=""
            onChange={(e) => { if (e.target.value) massenAktion('eigentum', e.target.value); }}
            disabled={massenLaeuft}
            className="rounded-lg border border-slate-300 bg-surface px-2 py-1 text-sm disabled:opacity-40"
          >
            <option value="">Eigentum setzen…</option>
            {EIGENTUM.map((b) => <option key={b} value={b}>{EIGENTUM_TEXT[b]}</option>)}
          </select>

          <select
            value=""
            onChange={(e) => { if (e.target.value) massenAktion('takt', e.target.value); }}
            disabled={massenLaeuft}
            className="rounded-lg border border-slate-300 bg-surface px-2 py-1 text-sm disabled:opacity-40"
          >
            <option value="">Prüftakt setzen…</option>
            {TAKTE.map((t) => <option key={t.wert} value={t.wert}>{t.label}</option>)}
          </select>

          <button
            onClick={() => setGewaehlt(new Set())}
            className="ml-auto flex items-center gap-1 text-sm text-brand-700 hover:text-brand-900"
          >
            <X size={13} /> Auswahl aufheben
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-surface">
        <table className="min-w-full text-sm">
          <TabellenKopf
            spalten={spalten}
            steuerung={tabelle}
            extra={['URLs', '']}
            vorne={
              <input
                type="checkbox"
                checked={alleDerSeite}
                onChange={seiteUmschalten}
                title="Alle auf dieser Seite"
                aria-label="Alle auf dieser Seite auswählen"
              />
            }
          />
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr><td colSpan={12} className="px-4 py-6 text-center text-slate-400">wird geladen…</td></tr>
            ) : tabelle.sichtbar.length === 0 ? (
              <tr><td colSpan={12} className="px-4 py-6 text-center text-slate-400">Nichts gefunden.</td></tr>
            ) : (
              tabelle.sichtbar.map((d) => (
                <React.Fragment key={d.id}>
                  <tr className={gewaehlt.has(d.id) ? 'bg-brand-50/60' : 'hover:bg-slate-50'}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={gewaehlt.has(d.id)}
                        onChange={() => umschalten(d.id)}
                        aria-label={`${d.unicodeName ?? d.name} auswählen`}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => setOffen(offen === d.id ? null : d.id)}
                        className="flex items-center gap-1.5 font-medium text-slate-800"
                      >
                        {offen === d.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {d.unicodeName ?? d.name}
                      </button>
                      {d.unicodeName && (
                        // Punycode danebenstellen: danach wird aufgelöst, und wer
                        // im DNS nachsieht, findet nur diese Form.
                        <code className="ml-5 block text-[11px] text-slate-400">{d.name}</code>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {/*
                        Verlinkt auf die Anmeldeseite, nicht auf die Startseite.
                        Wer hier klickt, will etwas am Eintrag ändern — und
                        landet sonst in der Werbung des Registrars.
                      */}
                      {d.provider
                        ? (anbieterLogin[d.provider.id]
                            ? <a
                                href={anbieterLogin[d.provider.id]}
                                target="_blank"
                                rel="noreferrer noopener"
                                onClick={(e) => e.stopPropagation()}
                                title={`Bei ${d.provider.name} anmelden`}
                                className="hover:text-brand-700 hover:underline"
                              >
                                {d.provider.name}
                              </a>
                            : d.provider.name)
                        : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{d.registrarTarget ?? '—'}</td>
                    <td className="px-4 py-2 text-right text-slate-600">{d._count?.subdomains ?? 0}</td>
                    <td className="px-4 py-2">
                      <select
                        value={d.monitorInterval}
                        onChange={(e) => tun(() => api.update(d.id, { monitorInterval: e.target.value as MonitorInterval }))}
                        className="rounded border border-slate-300 px-1.5 py-1 text-xs"
                      >
                        {TAKTE.map((t) => <option key={t.wert} value={t.wert}>{t.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2"><ErgebnisMarke result={d.lastResult} /></td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {d.expiresAt ? new Date(d.expiresAt).toLocaleDateString('de-DE') : '—'}
                    </td>
                    <td className="px-4 py-2"><FristAnzeige d={d} /></td>
                    <td className="px-4 py-2">
                      <BewertungsWahl
                        wert={d.ownership}
                        aendern={(w) => tun(() => api.update(d.id, { ownership: w }))}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <Link
                        to={`/domains/${d.id}/urls`}
                        onClick={(e) => e.stopPropagation()}
                        title="URL-Bestandsaufnahme: was kennt das Archiv, die Sitemap, Common Crawl?"
                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-700"
                      >
                        <Link2 size={13} /> URLs
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <label
                          title="Läuft der Verkehr über einen Cloudflare-Tunnel? Das setzt ein Mensch — der DNS-Befund ist nur ein Hinweis."
                          className="flex cursor-pointer items-center gap-1 text-xs text-slate-500"
                        >
                          <input
                            type="checkbox"
                            checked={d.cloudflareTunnel}
                            onChange={(e) => tun(() => api.update(d.id, { cloudflareTunnel: e.target.checked }))}
                          />
                          <Cloud size={13} className={clsx(d.cloudflareDetected && !d.cloudflareTunnel && 'text-amber-600')} />
                        </label>
                        <button
                          title="DNS auflösen"
                          onClick={() => tun(() => api.resolve(d.id), 'DNS aufgelöst.')}
                          className="rounded border border-slate-300 p-1 hover:bg-slate-50"
                        >
                          <RefreshCw size={13} />
                        </button>
                        <button
                          title="Jetzt prüfen"
                          onClick={() => tun(() => api.check(d.id), 'Geprüft.')}
                          className="rounded border border-slate-300 p-1 hover:bg-slate-50"
                        >
                          <Activity size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {offen === d.id && <SubdomainZeilen id={d.id} tun={tun} />}
                  {offen === d.id && <DomainDetail id={d.id} tun={tun} />}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Blaetterleiste blaettern={tabelle.blaettern} />
    </div>
  );
}

/**
 * Zonendatei oder Registrar-Export einlesen.
 *
 * IMMER ERST VORSCHAU. Eine Zonendatei ist keine Liste, die jemand für uns
 * geschrieben hat — sie ist der Ist-Zustand einer Zone, mit allem, was
 * darinsteht: Testaufbauten, alte Kundennamen, interne Dienste. Wer sie
 * ungesehen einliest, räumt danach auf.
 *
 * Das Format wird erkannt und nicht abgefragt: BIND, die TSV von
 * united-domains und die Semikolon-CSV von adns2 unterscheiden sich deutlich
 * genug, und ein Auswahlfeld "Format" wäre eine Frage, die der Rechner selbst
 * beantworten kann.
 */
function DnsImport({ d, tun }: {
  d: Domain;
  tun: (fn: () => Promise<unknown>, gut?: string) => Promise<void>;
}) {
  const [inhalt, setInhalt] = useState('');
  const [bericht, setBericht] = useState<DnsImportBericht | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const datei = async (f: File | undefined) => {
    if (!f) return;
    const text = await f.text();
    setInhalt(text);
    setBericht(null);
    await pruefen(text);
  };

  const pruefen = async (text: string) => {
    setLaeuft(true);
    await tun(async () => setBericht(await api.dnsImport(d.id, text, true)));
    setLaeuft(false);
  };

  const uebernehmen = async () => {
    setLaeuft(true);
    await tun(async () => setBericht(await api.dnsImport(d.id, inhalt, false)),
      'Zonendatei übernommen.');
    setLaeuft(false);
  };

  const passt = !bericht?.dateiDomain || bericht.dateiDomain === d.name;

  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        DNS einlesen
      </h3>

      <label className="mb-2 flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-2 py-3 text-sm text-slate-500 hover:border-brand-400 hover:text-brand-700">
        <Upload size={14} />
        Zonendatei oder CSV wählen
        <input
          type="file"
          accept=".txt,.csv,.zone,.tsv,text/plain,text/csv"
          className="hidden"
          onChange={(e) => datei(e.target.files?.[0])}
        />
      </label>

      {laeuft && <p className="text-xs text-slate-400">wird gelesen…</p>}

      {bericht && (
        <div className="space-y-2 text-xs">
          <p className="text-slate-600">
            Format <strong>{bericht.format.toUpperCase()}</strong>
            {bericht.dateiDomain && <> · Datei sagt <code>{bericht.dateiDomain}</code></>}
          </p>

          {!passt && (
            <p className="rounded-lg bg-amber-50 px-2 py-1.5 text-amber-800">
              <AlertTriangle size={11} className="mr-1 inline" />
              Die Datei gehört zu <code>{bericht.dateiDomain}</code>, hier steht{' '}
              <code>{d.name}</code>. Einträge fremder Zonen werden übersprungen.
            </p>
          )}

          <ul className="space-y-0.5 text-slate-600">
            <li>{bericht.eintraegeNeu} Einträge neu, {bericht.eintraegeAktualisiert} aktualisiert</li>
            <li>
              <strong>{bericht.subdomainsNeu} Subdomains neu</strong>, {bericht.subdomainsAktualisiert} aktualisiert
            </li>
            {bericht.proxiedAnzahl > 0 && (
              <li className="text-sky-700">
                {bericht.proxiedAnzahl} Einträge laufen über Cloudflares Proxy
                {bericht.cloudflareGesetzt && ' — Kennzeichen wird gesetzt'}
              </li>
            )}
            {bericht.fremd.length > 0 && (
              <li className="text-amber-700">{bericht.fremd.length} Einträge gehören zu anderen Zonen</li>
            )}
            {bericht.uebersprungen.length > 0 && (
              <li className="text-amber-700" title={bericht.uebersprungen.map((u) => `Zeile ${u.zeile}: ${u.grund}`).join('\n')}>
                {bericht.uebersprungen.length} Zeilen nicht lesbar
              </li>
            )}
          </ul>

          {bericht.subdomains.length > 0 && (
            <details className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1">
              <summary className="cursor-pointer text-slate-600">
                {bericht.subdomains.filter((x) => x.neu).length} neue Subdomains ansehen
              </summary>
              <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto">
                {bericht.subdomains.map((x) => (
                  <li key={x.fqdn} className={x.neu ? 'text-emerald-700' : 'text-slate-400'}>
                    {x.neu ? '+' : '·'} {x.fqdn} → {x.ziel || '—'}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {bericht.vorschau ? (
            <button
              onClick={uebernehmen}
              disabled={laeuft}
              className="w-full rounded-lg bg-brand-600 px-3 py-1.5 text-sm text-white hover:bg-brand-700 disabled:opacity-40"
            >
              Übernehmen
            </button>
          ) : (
            <p className="rounded-lg bg-emerald-50 px-2 py-1.5 text-emerald-800">Übernommen.</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Vertragsangaben und Notiz.
 *
 * Gespeichert wird beim Verlassen des Feldes, nicht bei jedem Tastendruck —
 * eine Notiz von zweihundert Zeichen wären sonst zweihundert Anfragen. Der
 * lokale Zustand hängt an der Id: klappt jemand eine andere Domain auf, muss
 * das Feld deren Inhalt zeigen und nicht den vorherigen.
 */
function VertragUndNotiz({ d, tun }: {
  d: Domain;
  tun: (fn: () => Promise<unknown>, gut?: string) => Promise<void>;
}) {
  const [notiz, setNotiz] = useState(d.notes ?? '');
  const [laufzeit, setLaufzeit] = useState(d.termMonths?.toString() ?? '');
  const [frist, setFrist] = useState(d.noticeDays?.toString() ?? '');

  // Leer heisst "nicht hinterlegt" und wird zu null — nicht zu 0. Eine
  // Kündigungsfrist von 0 Tagen ist eine Aussage, ein leeres Feld ist keine.
  const zahl = (w: string): number | null => {
    const t = w.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
  };

  const sichern = (feld: 'termMonths' | 'noticeDays', wert: string, alt: number | null | undefined) => {
    const n = zahl(wert);
    if (n === (alt ?? null)) return;
    tun(() => api.update(d.id, { [feld]: n }));
  };

  const stichtag = kuendbarBis(d);

  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Vertrag und Notiz
      </h3>

      <div className="mb-2 grid grid-cols-2 gap-2">
        <label className="text-xs text-slate-500">
          Laufzeit (Monate)
          <input
            type="number" min={1} max={120}
            value={laufzeit}
            onChange={(e) => setLaufzeit(e.target.value)}
            onBlur={() => sichern('termMonths', laufzeit, d.termMonths)}
            className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </label>
        <label className="text-xs text-slate-500">
          Kündigungsfrist (Tage)
          <input
            type="number" min={0} max={365}
            value={frist}
            onChange={(e) => setFrist(e.target.value)}
            onBlur={() => sichern('noticeDays', frist, d.noticeDays)}
            className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </label>
      </div>

      <p className="mb-3 text-xs text-slate-500">
        {stichtag
          ? <>Letzter Kündigungstag: <strong className="text-slate-700">{stichtag.toLocaleDateString('de-DE')}</strong></>
          : 'Letzter Kündigungstag: erst berechenbar, wenn Ablaufdatum und Frist stehen.'}
      </p>

      <textarea
        value={notiz}
        onChange={(e) => setNotiz(e.target.value)}
        onBlur={() => { if (notiz !== (d.notes ?? '')) tun(() => api.update(d.id, { notes: notiz || null })); }}
        rows={4}
        placeholder="Wofür ist die Domain, was ist damit vereinbart, wer hat sie bestellt…"
        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
      <p className="mt-1 text-xs text-slate-400">
        Wird beim Verlassen des Feldes gespeichert. Der Bestandsimport überschreibt sie nie.
      </p>
    </div>
  );
}

/**
 * Die Subdomains einer Domain — als eigene Zeilen in derselben Tabelle.
 *
 * WARUM ZEILEN UND NICHT EINE LISTE IM AUFKLAPPBEREICH: eine Subdomain ist
 * dasselbe wie eine Domain, nur eine Ebene tiefer. Sie hat eine IP, einen
 * Prüftakt, einen Zustand — dieselben Spalten. In einem eigenen Kasten daneben
 * stünden dieselben Angaben in einer anderen Form, und man müsste zweimal
 * lernen, wo was steht.
 *
 * Die Daten kommen aus derselben Abfrage wie der Detailbereich (`['domain', id]`);
 * React Query fasst beide zu einer Anfrage zusammen.
 */
function SubdomainZeilen({ id, tun }: {
  id: string;
  tun: (fn: () => Promise<unknown>, gut?: string) => Promise<void>;
}) {
  const { data, isLoading } = useQuery({ queryKey: ['domain', id], queryFn: () => api.get(id) });

  if (isLoading) {
    return (
      <tr className="bg-slate-50/60">
        <td colSpan={12} className="px-4 py-2 pl-12 text-xs text-slate-400">Subdomains werden geladen…</td>
      </tr>
    );
  }

  const subs: Subdomain[] = data?.subdomains ?? [];
  if (subs.length === 0) {
    return (
      <tr className="bg-slate-50/60">
        <td colSpan={12} className="px-4 py-2 pl-12 text-xs text-slate-400">
          Keine Subdomains hinterlegt. DNS lässt sich nicht auflisten — sie kommen von Hand
          oder aus einer importierten Zonendatei.
        </td>
      </tr>
    );
  }

  return (
    <>
      {subs.map((sd) => (
        <tr key={sd.id} className="bg-slate-50/60 hover:bg-slate-100/70">
          <td className="px-3 py-1.5" />
          <td className="px-4 py-1.5">
            <span className="flex items-center gap-1.5 pl-4 text-slate-600">
              <CornerDownRight size={12} className="shrink-0 text-slate-300" />
              {sd.fqdn}
              {/* ══ IN DIE ADRESSERHEBUNG EINBEZIEHEN ════════════════════════
                  Neben dem Namen und nicht in einer eigenen Spalte: es ist eine
                  Eigenschaft dieser Subdomain, und die Tabelle hat keine freie
                  Spalte, deren Überschrift dazu passte.

                  Bing kennt keine Domain-Eigenschaft — dort ist jede Subdomain
                  eine eigene Site, und die Abfrage für die Domain sieht sie
                  nicht. Bewusst ein Haken je Subdomain statt „alle": juni.com
                  hat 52 davon, und 52 Abfragen je Durchlauf für die vier, die
                  zählen, wäre die falsche Voreinstellung. */}
              <label
                className="ml-1 flex cursor-pointer items-center gap-1 whitespace-nowrap"
                title={
                  sd.inQuellen
                    ? 'Wird bei der Adresserhebung mitgefragt. Klicken zum Abwählen.'
                    : 'Nicht in der Adresserhebung. Klicken, um diese Subdomain mitzumessen — '
                      + 'nötig für Bing, das jede Subdomain als eigene Site führt.'
                }
              >
                <input
                  type="checkbox"
                  checked={Boolean(sd.inQuellen)}
                  onChange={(e) =>
                    tun(
                      () => api.updateSubdomain(sd.id, { inQuellen: e.target.checked }),
                      e.target.checked
                        ? `${sd.fqdn} wird bei der Adresserhebung mitgefragt.`
                        : `${sd.fqdn} wird nicht mehr mitgefragt.`,
                    )
                  }
                  className="h-3 w-3 rounded border-slate-300"
                />
                {sd.inQuellen && (
                  <span className="rounded bg-sky-100 px-1 text-[10px] text-sky-800">misst mit</span>
                )}
              </label>
            </span>
          </td>
          {/* Anbieter und Registrar-Ziel gehören der Domain, nicht der Subdomain. */}
          <td className="px-4 py-1.5" />
          {/*
            Steht unter „Ziel laut Registrar", stammt aber aus dem DNS-Befund --
            einen Registrar-Eintrag gibt es für eine Subdomain nicht. Der
            Unterschied ist im Titel benannt statt verwischt: bei Widerspruch
            hat der DNS-Befund recht, und man muss wissen, welchen man liest.
          */}
          <td
            className="px-4 py-1.5 text-xs text-slate-500"
            title={
              sd.lastDnsCheckAt
                ? `aufgelöst am ${new Date(sd.lastDnsCheckAt).toLocaleString('de-DE')}`
                : 'noch nicht aufgelöst'
            }
          >
            {sd.resolvedIp ?? sd.resolvedCname ?? '—'}
          </td>
          <td className="px-4 py-1.5" />
          <td className="px-4 py-1.5">
            <select
              value={sd.monitorInterval}
              onChange={(e) =>
                tun(() => api.updateSubdomain(sd.id, { monitorInterval: e.target.value as MonitorInterval }))
              }
              className="rounded border border-slate-200 bg-surface px-1 py-0.5 text-xs"
            >
              {TAKTE.map((t) => <option key={t.wert} value={t.wert}>{t.label}</option>)}
            </select>
          </td>
          <td className="px-4 py-1.5"><ErgebnisMarke result={sd.lastResult} /></td>
          <td className="px-4 py-1.5" />
          <td className="px-4 py-1.5" />
          <td className="px-4 py-1.5" />
          <td className="px-4 py-1.5" />
          <td className="px-4 py-1.5">
            <div className="flex items-center justify-end gap-1">
              <button
                title="Diese Subdomain jetzt prüfen"
                onClick={() => tun(() => api.checkSubdomain(sd.id), `${sd.fqdn} geprüft.`)}
                className="rounded border border-slate-200 p-1 hover:bg-surface"
              >
                <Activity size={12} />
              </button>
              <button
                title="Subdomain entfernen — löscht nur den Eintrag hier, nicht im DNS"
                onClick={() => {
                  if (!confirm(`${sd.fqdn} aus der Übersicht entfernen?\n\nIm DNS ändert das nichts.`)) return;
                  tun(() => api.removeSubdomain(sd.id), 'Subdomain entfernt.');
                }}
                className="rounded border border-slate-200 p-1 text-rose-600 hover:bg-surface"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

/** DNS-Einträge, Subdomains und Prüfverlauf einer Domain. */
function DomainDetail({ id, tun }: { id: string; tun: (fn: () => Promise<unknown>, gut?: string) => Promise<void> }) {
  const { data, isLoading } = useQuery({ queryKey: ['domain', id], queryFn: () => api.get(id) });
  const [neueSub, setNeueSub] = useState('');

  if (isLoading || !data) {
    return <tr><td colSpan={12} className="bg-slate-50 px-8 py-3 text-sm text-slate-400">wird geladen…</td></tr>;
  }

  return (
    <tr>
      <td colSpan={12} className="bg-slate-50 px-8 py-4">
        <div className="grid gap-6 lg:grid-cols-4">
          <VertragUndNotiz d={data} tun={tun} />
          <DnsImport d={data} tun={tun} />

          {/* ── Subdomains ─────────────────────────────────────────────── */}
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Subdomains
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                tun(async () => {
                  await api.addSubdomain(id, { label: neueSub.trim() });
                  setNeueSub('');
                }, 'Subdomain aufgenommen.');
              }}
              className="mb-2 flex gap-1"
            >
              <input
                value={neueSub}
                onChange={(e) => setNeueSub(e.target.value)}
                placeholder="www"
                className="w-24 rounded border border-slate-300 px-2 py-1 text-xs"
              />
              <span className="py-1 text-xs text-slate-400">.{data.unicodeName ?? data.name}</span>
              <button className="rounded border border-slate-300 bg-surface px-2 py-1 text-xs hover:bg-slate-50">
                <Plus size={12} />
              </button>
            </form>

            {/*
              Die Liste selbst steht jetzt als eigene Zeilen in der Tabelle --
              eine Subdomain hat dieselben Spalten wie eine Domain, und sie
              zweimal in zwei Formen zu zeigen hiesse, zweimal zu lernen, wo
              was steht. Hier bleibt nur, was zum Anlegen gehoert.
            */}
            <p className="text-xs text-slate-400">
              {(data.subdomains ?? []).length === 0 ? (
                <>
                  Keine eingetragen. <b>DNS lässt sich nicht auflisten</b> — es gibt keine
                  Abfrage „nenne alle Subdomains". Sie kommen von Hand oder aus einer
                  eingelesenen Zonendatei.
                </>
              ) : (
                <>{(data.subdomains ?? []).length} Subdomains — sie stehen oben in der Tabelle.</>
              )}
            </p>
          </div>

          {/* ── DNS ────────────────────────────────────────────────────── */}
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              DNS-Einträge
              {data.lastDnsCheckAt && (
                <span className="ml-2 font-normal normal-case text-slate-400">
                  {new Date(data.lastDnsCheckAt).toLocaleString('de-DE')}
                </span>
              )}
            </h3>
            {(data.records ?? []).length === 0 ? (
              <p className="text-xs text-slate-400">Noch nicht aufgelöst — Knopf mit dem Kreispfeil.</p>
            ) : (
              <ul className="space-y-0.5 font-mono text-[11px]">
                {(data.records ?? []).map((r) => (
                  <li key={r.id} className="flex gap-2">
                    <span className="w-12 shrink-0 text-slate-400">{r.type}</span>
                    <span className="break-all text-slate-700">{r.value}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Verlauf ────────────────────────────────────────────────── */}
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Letzte Prüfungen
            </h3>
            {(data.checks ?? []).length === 0 ? (
              <p className="text-xs text-slate-400">Noch keine.</p>
            ) : (
              <ul className="space-y-1">
                {(data.checks ?? []).slice(0, 8).map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="text-slate-400">
                      {new Date(c.checkedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                    <ErgebnisMarke result={c.result} />
                    {c.statusCode && <span className="text-slate-500">{c.statusCode}</span>}
                    {c.durationMs != null && <span className="text-slate-400">{c.durationMs} ms</span>}
                    {c.placeholderKind && (
                      <code className="rounded bg-amber-50 px-1 text-amber-800">{c.placeholderKind}</code>
                    )}
                    {c.tlsDaysLeft != null && (
                      <span className={clsx('flex items-center gap-0.5', c.tlsDaysLeft < 21 ? 'text-red-600' : 'text-slate-400')}>
                        <ShieldCheck size={10} /> {c.tlsDaysLeft} T
                      </span>
                    )}
                    {c.redirectTo && <span className="truncate text-slate-500">→ {c.redirectTo}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}
